import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';

import OrganizationProfileStep from './components/OrganizationProfileStep';
import BankIntegrationStep from './components/BankIntegrationStep';
import PayrollRulesStep from './components/PayrollRulesStep';
import SystemActivationStep from './components/SystemActivationStep';
import milestoneTrackingService from '../../services/milestoneTrackingService';

const QuickSetupConfigurationWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({
    organizationProfile: {},
    bankIntegration: {},
    payrollRules: {},
    systemActivation: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyId, setCompanyId] = useState(null);

  const steps = [
    { number: 1, title: 'Organization Profile', icon: 'Building2', description: 'Company details & structure' },
    { number: 2, title: 'Bank Integration', icon: 'CreditCard', description: 'Connect PNG financial institutions' },
    { number: 3, title: 'Payroll Rules', icon: 'Calculator', description: 'Configure PNG Employment Act rules' },
    { number: 4, title: 'System Activation', icon: 'CheckCircle', description: 'Validate & activate system' }
  ];

  const handleStepComplete = async (stepNumber, data) => {
    setSetupData(prev => {
      const updatedData = { ...prev };
      switch (stepNumber) {
        case 1:
          updatedData.organizationProfile = data;
          // Track organization profile milestone
          if (data?.companyId) {
            setCompanyId(data?.companyId);
            trackMilestoneCompletion(data?.companyId, 'organization_profile', data);
          }
          break;
        case 2:
          updatedData.bankIntegration = data;
          // Track employee import milestone (if applicable)
          if (companyId && data?.employeesImported) {
            trackMilestoneCompletion(companyId, 'employee_import', data);
          }
          break;
        case 3:
          updatedData.payrollRules = data;
          // Track compliance setup milestone
          if (companyId && data?.complianceConfigured) {
            trackMilestoneCompletion(companyId, 'compliance_setup', data);
          }
          break;
        case 4:
          updatedData.systemActivation = data;
          // Track team access milestone
          if (companyId && data?.teamAccessConfigured) {
            trackMilestoneCompletion(companyId, 'team_access', data);
          }
          break;
        default:
          break;
      }
      return updatedData;
    });

    if (stepNumber < 4) {
      setCurrentStep(stepNumber + 1);
    } else {
      handleFinalSubmit();
    }
  };

  const trackMilestoneCompletion = async (companyIdParam, milestoneType, data) => {
    try {
      // Mark milestone as completed
      await milestoneTrackingService?.completeMilestone(
        companyIdParam,
        milestoneType,
        { completedData: data }
      );

      // Note: Email is automatically sent by database trigger
      // The trigger detects milestone completion and queues the email
      console.log(`Milestone ${milestoneType} completed for company ${companyIdParam}`);
    } catch (err) {
      console.error('Error tracking milestone:', err);
      // Don't block user flow if milestone tracking fails
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Final validation and setup completion
      console.log('Quick setup completed with data:', setupData);
      
      // Navigate to dashboard after successful setup
      setTimeout(() => {
        navigate('/compliance-overview');
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel the setup? All progress will be lost.')) {
      navigate('/product-demo-onboarding-hub');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <OrganizationProfileStep
            data={setupData?.organizationProfile}
            onComplete={(data) => handleStepComplete(1, data)}
            onCancel={handleCancel}
          />
        );
      case 2:
        return (
          <BankIntegrationStep
            data={setupData?.bankIntegration}
            organizationInfo={setupData?.organizationProfile}
            onComplete={(data) => handleStepComplete(2, data)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 3:
        return (
          <PayrollRulesStep
            data={setupData?.payrollRules}
            organizationInfo={setupData?.organizationProfile}
            onComplete={(data) => handleStepComplete(3, data)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 4:
        return (
          <SystemActivationStep
            data={setupData?.systemActivation}
            allSetupData={setupData}
            onComplete={(data) => handleStepComplete(4, data)}
            onBack={handleBack}
            onCancel={handleCancel}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-3 rounded-xl shadow-lg">
              <Icon name="Zap" size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Quick Setup Configuration Wizard
          </h1>
          <p className="text-lg text-gray-600">
            Complete your organization setup in under 15 minutes without technical support
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        currentStep > step?.number
                          ? 'bg-emerald-600 text-white'
                          : currentStep === step?.number
                          ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-lg scale-110'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step?.number ? (
                        <Icon name="Check" size={24} />
                      ) : (
                        <Icon name={step?.icon} size={24} />
                      )}
                    </div>
                    <div className="text-center">
                      <p
                        className={`text-sm font-semibold ${
                          currentStep >= step?.number ? 'text-emerald-700' : 'text-gray-500'
                        }`}
                      >
                        {step?.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{step?.description}</p>
                    </div>
                  </div>
                  {index < steps?.length - 1 && (
                    <div className="flex-1 h-1 mx-4 mb-8">
                      <div
                        className={`h-full rounded transition-all ${
                          currentStep > step?.number ? 'bg-emerald-600' : 'bg-gray-200'
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Setup Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {renderStepContent()}
        </div>

        {/* Help Info */}
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-gradient-to-r from-amber-50 to-emerald-50 rounded-xl p-6 border border-amber-200">
            <div className="flex items-start">
              <Icon name="HelpCircle" size={24} className="text-amber-600 mr-3 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Need Help?
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Our intelligent wizard uses smart defaults and PNG-specific templates to accelerate your setup.
                  Real-time help tooltips and embedded video guides are available at each step.
                </p>
                <div className="flex gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200 flex-1">
                    <p className="font-semibold text-emerald-700 text-sm">⚡ Smart Defaults</p>
                    <p className="text-xs text-gray-600 mt-1">Pre-configured PNG Employment Act settings</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200 flex-1">
                    <p className="font-semibold text-emerald-700 text-sm">🔒 Secure Integration</p>
                    <p className="text-xs text-gray-600 mt-1">Bank-grade encryption for all connections</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200 flex-1">
                    <p className="font-semibold text-emerald-700 text-sm">✓ Auto-Validation</p>
                    <p className="text-xs text-gray-600 mt-1">Real-time compliance checking</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickSetupConfigurationWizard;